import type {IContentItem, Elements} from '@kontent-ai/delivery-sdk'
import {useState, useEffect, useRef, useMemo} from 'react'
import {useRouter} from 'next/router'
import {clsx} from 'clsx'
import type {Block} from '@/services/kontent-ai/types'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'

type QuantitySelectorContentItem = IContentItem<{
	quantity: Elements.TextElement
	elancoProductId: Elements.TextElement
	pearOfferId: Elements.TextElement
}>

type SizeSelectorContentItem = IContentItem<{
	size: Elements.TextElement
	quantitySelector: Elements.LinkedItemsElement<QuantitySelectorContentItem>
}>

export type SkuProviderContentItem = IContentItem<{
	sizeSelector: Elements.LinkedItemsElement<SizeSelectorContentItem>
}>

const findSkuIndices = (
	sizes: {
		size: string
		quantities: {
			quantity: string
			productId: string
			system: {id: string}
		}[]
		system: {id: string}
	}[],
	targetSku: string
): {sizeIdx: number; quantityIdx: number} | null => {
	if (!targetSku || typeof targetSku !== 'string' || !sizes.length) {
		return null
	}

	for (let sizeIdx = 0; sizeIdx < sizes.length; sizeIdx++) {
		const size = sizes[sizeIdx]
		// eslint-disable-next-line @typescript-eslint/prefer-optional-chain -- Runtime safety check
		if (!size || !size.quantities.length) continue

		for (
			let quantityIdx = 0;
			quantityIdx < size.quantities.length;
			quantityIdx++
		) {
			const quantity = size.quantities[quantityIdx]
			if (quantity?.productId === targetSku) {
				return {sizeIdx, quantityIdx}
			}
		}
	}
	return null
}

export const SkuProviderBlock: Block<SkuProviderContentItem> = ({
	block,
	page,
}) => {
	// we will extract the color from the new color function
	const {colorId: brandColor} = colorWithFallback({
		pageConfig: page.elements.config[0],
	})

	const borderColor = brandColor ? `border-${brandColor}` : 'border-primary'
	const selectBorderColor = brandColor
		? `border-${brandColor} border-3`
		: 'border-primary border-3'

	const router = useRouter()

	const sizes = useMemo(
		() =>
			(Array.isArray(block.elements.sizeSelector)
				? block.elements.sizeSelector
				: []
			).map((sizeItem) => ({
				size: sizeItem.elements.size,
				quantities: (Array.isArray(sizeItem.elements.quantitySelector)
					? sizeItem.elements.quantitySelector
					: []
				).map((qtyItem) => ({
					quantity: qtyItem.elements.quantity,
					productId: qtyItem.elements.elancoProductId,
					system: qtyItem.system,
					pearOfferId: qtyItem.elements.pearOfferId,
				})),
				system: sizeItem.system,
			})),
		[block.elements.sizeSelector]
	)

	const [selectedSizeIdx, setSelectedSizeIdx] = useState(0)
	const [selectedQuantityIdx, setSelectedQuantityIdx] = useState(0)
	const [isInitialized, setIsInitialized] = useState(false)
	const isFirstRender = useRef(true)
	const previousSizeIdx = useRef(0)

	const selectedSize = useMemo(
		() => sizes[selectedSizeIdx],
		[sizes, selectedSizeIdx]
	)
	const quantities = useMemo(
		() => selectedSize?.quantities ?? [],
		[selectedSize]
	)
	const selectedQuantity = useMemo(
		() => quantities[selectedQuantityIdx],
		[quantities, selectedQuantityIdx]
	)
	const elancoProductId = useMemo(
		() => selectedQuantity?.productId,
		[selectedQuantity]
	)
	const showSizeSection = useMemo(() => sizes.length > 1, [sizes.length])

	const pearOfferId = useMemo(
		() => selectedQuantity?.pearOfferId,
		[selectedQuantity]
	)

	useEffect(() => {
		if (isInitialized) return

		if (!sizes.length || !sizes[0]?.quantities?.length) {
			setIsInitialized(true)
			return
		}

		const querySku =
			typeof router.query.sku === 'string' ? router.query.sku : undefined
		let initialSizeIdx = 0
		let initialQuantityIdx = 0
		let shouldUpdateUrl = false

		if (querySku) {
			const indices = findSkuIndices(sizes, querySku)
			if (indices) {
				initialSizeIdx = indices.sizeIdx
				initialQuantityIdx = indices.quantityIdx
			} else {
				shouldUpdateUrl = true
			}
		} else {
			shouldUpdateUrl = true
		}

		setSelectedSizeIdx(initialSizeIdx)
		setSelectedQuantityIdx(initialQuantityIdx)
		previousSizeIdx.current = initialSizeIdx
		setIsInitialized(true)

		if (shouldUpdateUrl) {
			const defaultSku =
				sizes[initialSizeIdx]?.quantities[initialQuantityIdx]?.productId
			if (defaultSku) {
				void router.replace(
					{
						pathname: router.asPath.split('?')[0],
						query: {sku: defaultSku},
					},
					undefined,
					{shallow: true}
				)
			}
		}
	}, [router, sizes, isInitialized])

	useEffect(() => {
		if (isInitialized && isFirstRender.current) {
			isFirstRender.current = false
		} else if (isInitialized && !isFirstRender.current) {
			if (previousSizeIdx.current !== selectedSizeIdx) {
				setSelectedQuantityIdx(0)
				previousSizeIdx.current = selectedSizeIdx
			}
		}
	}, [isInitialized, selectedSizeIdx])

	useEffect(() => {
		if (!isInitialized || !elancoProductId) return

		const currentSku = router.query.sku
		if (currentSku !== elancoProductId) {
			void router.replace(
				{
					pathname: router.asPath.split('?')[0],
					query: {sku: elancoProductId, offerId: pearOfferId},
				},
				undefined,
				{shallow: true}
			)
		}
	}, [elancoProductId, pearOfferId, router, isInitialized])

	return (
		<div className="flex flex-col items-start gap-6">
			{/* Size Selector */}
			{showSizeSection ? (
				<div>
					<div className="mb-2 text-sm font-semibold">Pet weight</div>
					<div className="flex flex-wrap gap-4">
						{sizes.map((s, idx) => (
							<button
								className={clsx(
									'flex min-w-[64px] items-center justify-center rounded-lg border px-3 py-2 text-sm font-bold leading-5 transition-all duration-150',
									borderColor,
									selectedSizeIdx === idx
										? [selectBorderColor, 'shadow']
										: []
								)}
								data-kontent-element-codename="size"
								data-kontent-item-id={s.system.id}
								key={s.size}
								onClick={() => {
									setSelectedSizeIdx(idx)
									setSelectedQuantityIdx(0)
								}}
								type="button"
							>
								{s.size}
							</button>
						))}
					</div>
				</div>
			) : null}

			{/* Quantity Selector */}
			{quantities.length > 0 && (
				<div>
					<div className="mb-2 text-sm font-semibold">Quantity</div>
					<div className="flex flex-wrap gap-4">
						{quantities.map((q, idx) => (
							<button
								className={clsx(
									'flex min-w-[64px] items-center justify-center rounded-lg border px-3 py-2 text-sm font-bold leading-5 transition-all duration-150',
									borderColor,
									selectedQuantityIdx === idx
										? [selectBorderColor, 'shadow']
										: []
								)}
								data-kontent-element-codename="quantity"
								data-kontent-item-id={q.system.id}
								key={q.quantity}
								onClick={() => {
									setSelectedQuantityIdx(idx)
								}}
								type="button"
							>
								{q.quantity}
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	)
}
